import * as reduxApi from 'src/utils/redux-api-v2';
import type {Dispatch, ThunkAction} from 'src/reducers';
import {ANALYTICS_API_V4} from 'src/utils/analytics-api-migration';
import type {ApiOptions, ApiPostOptions} from 'src/utils/redux-api';
import type {
  RecruitmentFilters,
  SavedDashboardList,
  SelectedRecruiterFilterColumnsData,
} from 'src/types/analytics-genesis';
import type {DateRangeWithTimezone} from '@spaced-out/ui-design-system/lib/types';
import {thunkify as flow} from 'src/utils/thunks';

import {fetching, key} from 'src/utils/redux';
import {recruiterTableColumnsQuery} from 'src/utils/analytics-genesis';


const analyticsOptions: ApiOptions = {apiPath: ANALYTICS_API_V4};
const analyticsApiOptions: ApiPostOptions = {apiPath: ANALYTICS_API_V4};

export const SET_CURRENT_DASHBOARD_ID =
  'recruiter-productivity/set-current-dashboard';
export const SET_SELECTED_FILTERS =
  'recruiter-productivity/set-selected-filters';
export const SET_SELECTED_DATE_RANGE =
  'recruiter-productivity/set-selected-date-range';
export const CLEAR_SELECTED_FILTERS =
  'recruiter-productivity/clear-selected-filters';
export const RECEIVE_SAVED_DASHBOARDS =
  'recruiter-productivity/receive-saved-dashboards';
export const RECEIVE_RECRUITER_TABLE_COLUMNS =
  'recruiter-productivity/receive-recruiter-table-columns';

type SetCurrentDashboardIdAction = {
  type: typeof SET_CURRENT_DASHBOARD_ID;
  payload: string | null | undefined;
};

type SetSelectedFiltersAction = {
  type: typeof SET_SELECTED_FILTERS;
  payload: RecruitmentFilters;
};

type SetDateRangeAction = {
  type: typeof SET_SELECTED_DATE_RANGE;
  payload: DateRangeWithTimezone;
};

type ReceiveSavedDashboardsAction = {
  type: typeof RECEIVE_SAVED_DASHBOARDS;
  payload: SavedDashboardList;
};

type ClearSelectedFiltersAction = {
  type: typeof CLEAR_SELECTED_FILTERS;
};

type ReceiveRecruiterTableColumnAction = {
  type: typeof RECEIVE_RECRUITER_TABLE_COLUMNS;
  payload: SelectedRecruiterFilterColumnsData;
};

export type RecruiterProductivityActions =
  | SetSelectedFiltersAction
  | ReceiveSavedDashboardsAction
  | SetCurrentDashboardIdAction
  | ClearSelectedFiltersAction
  | SetDateRangeAction
  | ReceiveRecruiterTableColumnAction;

export const setCurrentDashboardId = (
  payload: string,
): SetCurrentDashboardIdAction => ({
  type: SET_CURRENT_DASHBOARD_ID,
  payload,
});

export const setSelectedFilters = (
  payload: RecruitmentFilters,
): SetSelectedFiltersAction => ({
  type: SET_SELECTED_FILTERS,
  payload,
});

export const setDateRange = (
  payload: DateRangeWithTimezone,
): SetDateRangeAction => ({
  type: SET_SELECTED_DATE_RANGE,
  payload,
});

export const receiveSavedDashboards = (
  payload: SavedDashboardList,
): ReceiveSavedDashboardsAction => ({
  type: RECEIVE_SAVED_DASHBOARDS,
  payload,
});

export const clearSelectedFilters = (): ClearSelectedFiltersAction => ({
  type: CLEAR_SELECTED_FILTERS,
});

export const receiveRecruiterTableColumnSelection = (
  payload: SelectedRecruiterFilterColumnsData,
): ReceiveRecruiterTableColumnAction => ({
  type: RECEIVE_RECRUITER_TABLE_COLUMNS,
  payload,
});

export const getSavedRecruiterDashboards: () => ThunkAction<unknown> =
  () => async (dispatch: Dispatch) => {
    try {
      const response = await dispatch(
        reduxApi.get(
          `/crm/save-filter`,
          {
            product: 'crm',
            dashboard: 'recruiter-productivity',
          },
          {apiPath: '/api/v1/analytics'},
        ),
      );
      const {
        filters,
      }: {
        filters: SavedDashboardList;
      } = response;
      await dispatch(receiveSavedDashboards(filters));

      const defaultDashboard = filters.find(({is_default}) => is_default);
      if (defaultDashboard) {
        const {id = '', filters = {}} = defaultDashboard;
        // @ts-expect-error - TS2339 - Property 'job_status' does not exist on type 'RecruitmentFilters | CandidateSourcePerformanceFilters | HiringPipelineFilters | NPSFilters | ReferralsFilters'.
        const {job_status = []} = filters;
        await dispatch(setCurrentDashboardId(id));
        await dispatch(
          setSelectedFilters({
            job_status,
          }),
        );
      }
    } catch (error: any) {
      console.error(
        'Error fetching saved dashboard filter for recruiters:',
        error,
      );
    }
  };

export const getRecruiterTableFilterColumn: () => ThunkAction<unknown> = flow(
  key(() => 'getRecruiterTableFilterColumn'),
  fetching(),
)(() => async (dispatch: Dispatch) => {
  try {
    const payload: SelectedRecruiterFilterColumnsData = await dispatch(
      reduxApi.get('/crm/column-selection', recruiterTableColumnsQuery, {
        apiPath: '/api/v1/analytics',
      }),
    );
    dispatch(receiveRecruiterTableColumnSelection(payload));
  } catch (error: any) {
    console.error('Error fetching recruiter table column filter:', error);
  }
});

export const saveRecruiterFilterColumns: (
  columnData: SelectedRecruiterFilterColumnsData,
) => ThunkAction<unknown> = flow(
  key(
    (columnData) => `saveRecruiterFilterColumns-${JSON.stringify(columnData)}`,
  ),
  fetching(),
)(
  (columnData) => async (dispatch: Dispatch) =>
    dispatch(
      reduxApi.post(
        '/crm/column-selection',
        columnData,
        {},
        {apiPath: '/api/v1/analytics'},
      ),
    ),
);
